import { Autocomplete, Button, Dialog, DialogActions, DialogContent, DialogTitle, Grid, TextField } from "@mui/material";
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { format } from 'date-fns';
import { enqueueSnackbar } from "notistack";
import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import Iconify from "src/components/iconify";
import { useServerRequest } from "src/hooks/useServerRequest";
import { useLocales } from "src/locales";
import { selectFilteredData, selectForeignBeneficiaries, selectLocalBeneficiaries, selectSelectedCompanyIds } from "src/redux/selectors";
import { addTransfer } from "src/redux/slices/transfersSlice";
import { Company } from "src/types/data";
import { formatRib } from "src/utils/format/format_rib";

interface RibDialogProps {
  open: boolean;
  onClose: () => void;
}
const types = [
  { id: 0, label: 'Local Beneficiary' },
  { id: 1, label: 'Foreing Beneficiary' },
];

type Beneficiary = {
  id: number;
  name: string;
};

export const AddTransfer = ({ open, onClose }: RibDialogProps) => {
  const filteredData = useSelector(selectFilteredData);
  const companiesIDs = useSelector(selectSelectedCompanyIds);
  const locals = useSelector(selectLocalBeneficiaries);
  const foreings = useSelector(selectForeignBeneficiaries);
  const [type, setType] = useState<number>(types[0].id);
  const [company, setCompany] = useState<number | null>(null);
  const [rib, setRib] = useState<number | null>(null);
  const num = Math.floor(Math.random() * 10000);
  const d = new Date();
  const [number, setNumber] = useState<string | null>(`${num}/${d.getFullYear()}`);
  const [dateEmm, setDateEmm] = useState<Date | null>(null);
  const [name, setName] = useState<number | null>(null);
  const [amount, setAmount] = useState<string | null>(null);
  const [motif, setMotif] = useState<string | null>(null);
  const [note, setNote] = useState<string | null>(null);
  const [attestation, setAttestation] = useState<File | null>(null);
  const dispatch = useDispatch();
  const { makeRequest } = useServerRequest();
  const [ribs, setRibs] = useState<{ id: number; label: string }[]>([]);
  const [names, setNames] = useState<Beneficiary[]>([]);
  const { t } = useLocales();
  const companies = filteredData
    .filter((comp: Company) => comp.user_role === "admin")
    .map((comp: Company) => ({
      id: comp.id,
      label: comp.name,
    }));

  const [errors, setErrors] = useState({
    company: false,
    rib: false,
    number: false,
    dateEmm: false,
    name: false,
    amount: false,
  });

  useEffect(() => {
    if (type === 0 && locals) {
      setNames(locals.map((local) => ({ id: local.id, name: local.name })));
    } else if (foreings) {
      setNames(foreings.map((foreign) => ({ id: foreign.id, name: foreign.name })));
    }
  }, [type, locals, foreings]);

  const handleCompany = (
    event: any,
    newValue: { id: number; label: string } | null
  ) => {
    setCompany(newValue?.id ?? null);

    if (newValue) {
      const selectedCompany = filteredData.find(
        (comp) => comp.id === newValue.id
      );
      const rbs =
        selectedCompany?.ribs.map((rb) => ({
          id: rb.id,
          label: `${formatRib(rb.rib)} - ${rb.bref}`,
        })) || [];

      setRibs(rbs);
      setRib(rbs[0]?.id ?? null);
    } else {
      setRibs([]);
    }
  };

  const handleAttestationChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      setAttestation(e.target.files[0]);
    }
  };

  const handleFileUploadClick = () => {
    document.getElementById("file-upload")?.click();
  };

  const handleAmount = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { value } = e.target;
    let formattedValue = value.replace(/[^0-9.,]/g, '');
    formattedValue = formattedValue.replace(',', '.');
    const parts = formattedValue.split('.');
    if (parts.length > 2) {
      formattedValue = `${parts[0]}.${parts.slice(1).join('')}`;
    }

    setAmount(formattedValue);
  };

  const handleSubmit = async () => {
    // Validation logic
    const newErrors = {
      company: company === null,
      rib: rib === null,
      number: !number,
      dateEmm: dateEmm === null,
      name: !name,
      amount: !amount,
    };

    setErrors(newErrors);

    // If there are no errors, proceed with form submission logic
    const hasErrors = Object.values(newErrors).some((error) => error);
    if (!hasErrors) {
      const formattedDate = dateEmm ? format(dateEmm, 'yyyy-MM-dd HH:mm:ss') : null;

      // Create FormData to handle file upload
      const formData = new FormData();
      formData.append('type', String(type));
      formData.append('company_id', String(company));
      formData.append('bank_id', String(rib));
      formData.append('numero', number || '');
      formData.append('date_d_emition', formattedDate ? formattedDate.toString() : '');
      formData.append('beneficiere_id', String(name) || '');
      formData.append('amount', amount || '');
      if (motif) formData.append('motif', motif);
      if (note) formData.append('remarque', note);
      if (attestation) formData.append('attachement', attestation);

      try {
        const response = await makeRequest(`/saveTransfer`, {
          method: 'POST',
          data: formData,
          headers: {
            'Content-Type': 'multipart/form-data',
          }
        }, '-');

        if (response.success && response.data) {
          console.log(response.data.transfer);

          dispatch(addTransfer({ transfer: response.data.transfer, selectedCompanyIds: companiesIDs }));
          enqueueSnackbar(t('transfers.m2'), { variant: 'success' });
          onClose();
        }
      } catch (error) {
        enqueueSnackbar(t('transfers.m3'), { variant: 'error' });
      }
    }
  };


  return (
    <Dialog open={open} disableEscapeKeyDown onClose={(_, reason) => reason !== 'backdropClick' && onClose()} maxWidth="sm" fullWidth>
      <DialogTitle>{t('m.t.benef')}</DialogTitle>
      <DialogContent>
        <Grid container spacing={2} sx={{ pt: "10px" }}>
          <Grid item xs={12} md={6}>
            <Autocomplete
              options={types}
              getOptionLabel={(option) => option.label}
              value={types[type]}
              onChange={(event, newValue) => setType(newValue!.id)}
              renderInput={(params) => <TextField {...params} label="Select Type" />}
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <Autocomplete
              options={companies}
              getOptionLabel={(option) => option.label}
              value={company ? companies.find((comp) => comp.id === company) : null}
              onChange={handleCompany}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label={t('common.input.selectCompany')}
                  error={errors.company}
                  helperText={errors.company && t('common.input.companies.error')}
                />
              )}
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <Autocomplete
              options={ribs}
              getOptionLabel={(option) => option.label}
              value={rib ? ribs.find((rb) => rb.id === rib) : null}
              onChange={(event, newValue) => setRib(newValue?.id ?? null)}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label={t('common.input.selectRib')}
                  error={errors.rib}
                  helperText={errors.rib && t('common.input.rib.error2')}
                />
              )}
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              label={t('common.input.number.label')}
              value={number || ""}
              onChange={(e) => setNumber(e.target.value)}
              error={errors.number}
              helperText={errors.number && t('common.input.number.error')}
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <DatePicker
              label={t('common.input.takenDate.label')}
              value={dateEmm || null}
              onChange={(newValue) => setDateEmm(newValue)}
              slotProps={{
                textField: {
                  fullWidth: true,
                  error: errors.dateEmm,
                  helperText: errors.dateEmm && t('common.input.takenDate.error1'),
                },
              }}
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <Autocomplete
              options={names}
              getOptionLabel={(option) => option.name}
              value={names.find((nm) => nm.id === name) || null}
              onChange={(event, newValue) => {
                setName(newValue ? newValue.id : null);
              }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label={t('transfers.benefs.name')}
                  error={errors.name}
                  helperText={errors.name && t('common.input.benef.error2')}
                />
              )}
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              label={t('common.input.amount.label2')}
              value={amount || ""}
              onChange={handleAmount}
              error={errors.amount}
              helperText={errors.amount && t('common.input.amount.error2')}
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <Button
              fullWidth
              variant={!attestation ? "outlined" : "contained"}
              onClick={handleFileUploadClick}
              sx={{ p: 1.5, backgroundColor: "success" }}
              startIcon={<Iconify icon="mingcute:upload-3-fill" />}
            >
              Upload Attestation
            </Button>
            <input
              id="file-upload"
              type="file"
              accept=".pdf,.jpg,.png"
              style={{ display: "none" }}
              onChange={handleAttestationChange}
            />
          </Grid>
          <Grid item xs={6}>
            <TextField
              fullWidth
              label="Motif"
              value={motif || ""}
              onChange={(e) => setMotif(e.target.value)}
            />
          </Grid>
          <Grid item xs={6}>
            <TextField
              fullWidth
              label="Note"
              value={note || ""}
              onChange={(e) => setNote(e.target.value)}
            />
          </Grid>
        </Grid>
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose} variant="outlined" color="inherit">
          {t('common.button.action.cancel')}
        </Button>
        <Button onClick={handleSubmit} variant="contained">
          {t('common.button.action.save')}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
